import { datadogRum } from "@datadog/browser-rum";
import moment from "moment";

import { BTAdminBuilderPreference } from "@btnet/services-btnet/handlers/models/bTAdminBuilderPreference";
import type { CoreSpaConfiguration } from "@btnet/services-btnet/handlers/models/coreSpaConfiguration";
import { OptIn } from "@btnet/services-btnet/handlers/models/optIn";
import type { SawmillConfiguration } from "@btnet/services-btnet/handlers/models/sawmillConfiguration";
import type { TimeClockSettingsResponse } from "@btnet/services-btnet/handlers/models/timeClockSettingsResponse";
import { CurrencyLocale, isNullOrUndefined } from "@btnet/utilities-core";

import {
    type AccountingIntegrationType,
    type BTLoginTypes,
    type JobDescriptionType,
} from "types/enum";
import UserTabPermissions from "types/UserTabPermissions/UserTabPermissions";

import {
    type ServiceCloudConfig,
    ServiceCloudSettings,
} from "commonComponents/utilities/ClientChat/ServiceCloudTypes";
import {
    type IAlert,
    type IHeaderAlertExtraData,
} from "commonComponents/utilities/HeaderAlerter/HeaderAlerter.types";

import { type default as AppProviderAPI } from "./AppProvider.api.json";

export class SpaInfo {
    isSpa: boolean | null;
}

export class BrandingInfo {
    constructor(data: typeof AppProviderAPI.brandingInfo) {
        this.primaryColor = data.primaryColor;
    }

    primaryColor: string;
}

export class OrgLinkInfo {
    constructor(data: (typeof AppProviderAPI.userInfo.orgLinks)[0]) {
        this.orgId = data.orgId;
        this.orgLinkId = data.orgLinkId;
        this.orgName = data.orgName;
        this.isClosed = data.isClosed;
    }

    orgId: number;
    orgLinkId: number;
    orgName?: string;
    isClosed?: boolean;
}

export class RoleInfo {
    constructor(data: any) {
        this.jobDescription = data.jobDescription;
        this.roleName = data.roleName;
        this.roleIsSystemDefault = data.roleIsSystemDefault;
    }

    jobDescription: JobDescriptionType;
    roleName: string;
    roleIsSystemDefault: boolean;
}

export class IntegrationUserProfile {
    constructor(data: typeof AppProviderAPI.integrationUserProfile) {
        this.userId = data.userId;
        this.username = data.username;
        this.fullName = data.fullName;
        this.firstName = data.firstName;
        this.isAdmin = data.isAdmin;
        this.isBtChampion = data.isBtChampion;
        this.userCreatedAt = data.userCreatedAt ? moment.unix(data.userCreatedAt) : null;
        this.userRole = data.userRole;
        this.hasActiveJobs = data.hasActiveJobs;
        this.canAddDailyLogs = data.canAddDailyLogs;
        this.canAddToDos = data.canAddToDos;
        this.canAddScheduleItems = data.canAddScheduleItems;
        this.canAddInternalUsers = data.canAddInternalUsers;
        this.canEditFiles = data.canEditFiles;
    }

    userId: number;
    username: string;
    fullName: string;
    firstName: string;
    isAdmin: boolean;
    isBtChampion: boolean;
    userCreatedAt: moment.Moment | null;
    userRole: string | null;
    hasActiveJobs: boolean;
    canAddDailyLogs: boolean;
    canAddToDos: boolean;
    canAddScheduleItems: boolean;
    canAddInternalUsers: boolean;
    canEditFiles: boolean;
}

export class IntegrationBuilderProfile {
    constructor(data: typeof AppProviderAPI.integrationBuilderProfile) {
        this.builderId = data.builderId;
        this.builderName = data.builderName;
        this.accountSetupDate = moment.unix(data.accountSetupDate);
        this.isConnectedToAccounting = data.isConnectedToAccounting;
        this.accountingIntegrationType = data.accountingIntegrationType;
        this.hasOnboardingContact = data.hasOnboardingContact;
        this.subscriptionPlan = data.subscriptionPlan;
        this.isPaymentProcessingLinked = data.isPaymentProcessingLinked;
        this.hasCustomCostCodes = data.hasCustomCostCodes;
        this.activeInternalUserCount = data.activeInternalUserCount;
    }

    builderId: number;
    builderName: string;
    accountSetupDate: moment.Moment;
    isConnectedToAccounting: boolean;
    accountingIntegrationType: AccountingIntegrationType;
    hasOnboardingContact: boolean;
    subscriptionPlan: string;
    isPaymentProcessingLinked: boolean;
    hasCustomCostCodes: boolean;
    activeInternalUserCount: number;
}

export class TimeLocale {
    constructor(data: { is24HourClock: boolean; format: string; timeZoneName: string }) {
        this.timeZoneName = data.timeZoneName;
        this.is24HourClock = data.is24HourClock;
        this.format = data.format;
    }

    static default = new TimeLocale({
        is24HourClock: false,
        format: "h:mm a",
        timeZoneName: "CST",
    });

    timeZoneName: string;
    is24HourClock: boolean;
    format: string;
}

export interface IDateLocale {
    dateFormat: string;
    dateTimeFormat: string;
    weekFormat: string;
    monthFormat: string;

    /** List of acceptable format strings for date inputs */
    dateInputFormats: string[] | undefined;
}

export class DateLocale implements IDateLocale {
    constructor(builderInputDateFormat: string) {
        const separators = ["-", ".", " ", "/"];
        // all values are used for parsing and first value is used for formatting
        // this allows users to type in their locale culture format (example: M-D-YYYY for USA)
        this.dateInputFormats = [
            this.dateFormat,
            "LL",
            ...separators.flatMap((separator) => {
                const withSeparator = builderInputDateFormat.replace(/[-./ ]/g, separator);
                const nonLeadingZero = withSeparator.replace("MM", "M").replace("DD", "D");
                const leadingZero = nonLeadingZero.replace("M", "MM").replace("D", "DD");
                return [nonLeadingZero, leadingZero];
            }),
        ];
        this.builderInputDateFormat = builderInputDateFormat;
    }

    static default = new DateLocale("MM-DD-YYYY");

    builderInputDateFormat: string;

    // These are all hardcoded for now and follow AP standards. If we ever want builder-specific
    // formats they should be provided server side
    dateFormat = "ll";
    dateTimeFormat = "ll HH:mm";
    weekFormat = "wo [week of] gggg";
    monthFormat = "MMM YYYY";

    /** List of acceptable format strings for date inputs */
    dateInputFormats: string[] | undefined;
}

export { CurrencyLocale };

export class BuilderLocale {
    constructor(data: typeof AppProviderAPI.builderInfo.locale) {
        this.currencyLocale = new CurrencyLocale(data.currencyLocale);
        this.dateLocale = new DateLocale(data.dateFormat);
        this.timeLocale = new TimeLocale(data.timeLocale);
    }

    currencyLocale: CurrencyLocale;
    dateLocale: DateLocale;
    timeLocale: TimeLocale;
}

export class FirebaseConfig {
    constructor(data: typeof AppProviderAPI.thirdPartyInfo.firebaseConfig) {
        this.apiKey = data.apiKey;
        this.authDomain = data.authDomain;
        this.databaseURL = data.databaseURL;
        this.projectId = data.projectId;
        this.storageBucket = data.storageBucket;
        this.messagingSenderId = data.messagingSenderId;
        this.appId = data.appId;
        this.measurementId = data.measurementId;
    }

    apiKey: string;
    authDomain: string;
    databaseURL: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId: string;
}

export class ThirdPartyInfo {
    constructor(data: typeof AppProviderAPI.thirdPartyInfo) {
        this.googleMapsApiKey = data.googleMapsApiKey;
        this.sentryCurrentEnvironmentReleaseId = data.sentryCurrentEnvironmentReleaseId;
        this.firebaseConfig = data.firebaseConfig ? new FirebaseConfig(data.firebaseConfig) : null;
        this.sardineClientId = data.sardineClientId;
        this.sardineSessionKey = data.sardineSessionKey;
    }

    googleMapsApiKey: string;
    /** Can be null in certain environments, like dev */
    sentryCurrentEnvironmentReleaseId: string | null;
    firebaseConfig: FirebaseConfig | null;
    sardineClientId: string | null;
    sardineSessionKey: string | null;
}

export class SearchServiceConfiguration {
    apiBaseUrl: string;
    authAudience: string;
}

export class EnvironmentInfo {
    constructor(data: typeof AppProviderAPI.environmentInfo) {
        this.isProduction = data.isProduction;
        this.name = data.name;
        this.color = data.color;
        this.supportMobileEphemeralLogin = data.supportMobileEphemeralLogin;
        this.auth0Url = data.auth0Url;
        this.quartzPath = data.quartzPath;
        this.isPublicEph = data.isPublicEph;
        this.infraCreationDate = data.infraCreationDate
            ? new Date(data.infraCreationDate)
            : undefined;
    }

    isProduction: boolean;
    name: string;
    color: string;
    supportMobileEphemeralLogin: boolean;
    auth0Url: string;
    quartzPath: string;
    isPublicEph: boolean;
    infraCreationDate?: Date;
}

export class AccountFlags {
    private accountFlags: Record<BTAdminBuilderPreference, boolean>;

    constructor(data: Record<string, boolean> | undefined) {
        this.accountFlags = {} as Record<BTAdminBuilderPreference, boolean>;

        Object.values(BTAdminBuilderPreference).forEach((key) => {
            const value = data?.[key] ?? false;
            this.accountFlags[key] = value;

            datadogRum.addFeatureFlagEvaluation(key, value);
        });
    }

    public hasFlag(flag: keyof typeof BTAdminBuilderPreference): boolean {
        return this.accountFlags[BTAdminBuilderPreference[flag]] ?? false;
    }
}

export type BuilderFlags = {
    -readonly [K in keyof typeof BTAdminBuilderPreference]: boolean;
};

let useAccountFlagsAsSourceDefault = false;

export function SetUseAccountFlagsAsSourceDefault(useAccountFlagsAsSource: boolean): void {
    useAccountFlagsAsSourceDefault = useAccountFlagsAsSource;
}

export function GetBuilderFlags(
    data: Record<string, boolean> | undefined,
    accountFlagsData?: Record<string, boolean>,
    useAccountFlagsAsSource: boolean = useAccountFlagsAsSourceDefault,
): BuilderFlags {
    // Feature Flag Migration: when the app default is ON, source the deprecated `flags` proxy from
    // `accountFlags` (DB-sourced) instead of `flags` (enum-sourced). Both use the same camelCase
    // serialized keys, so the ~245 existing `.flags.xxx` consumers read the DB values unchanged.
    const sourceData =
        useAccountFlagsAsSource && !isNullOrUndefined(accountFlagsData) ? accountFlagsData : data;

    if (sourceData === undefined) {
        const defaultFlags = {} as BuilderFlags;
        Object.keys(BTAdminBuilderPreference).forEach((key) => {
            (defaultFlags as Record<string, boolean>)[key] = false;
        });
        return defaultFlags;
    }

    const flags = {} as BuilderFlags;

    Object.keys(BTAdminBuilderPreference).forEach((key) => {
        const builderFlagKey = key as keyof BuilderFlags;
        const keyExistsInData = builderFlagKey in sourceData;

        Object.defineProperty(flags, builderFlagKey, {
            get() {
                if (!keyExistsInData) {
                    return true;
                }
                datadogRum.addFeatureFlagEvaluation(builderFlagKey, sourceData[builderFlagKey]);
                return sourceData[builderFlagKey];
            },
            enumerable: true,
            configurable: true,
        });
    });

    return flags;
}

export class BuilderConfigFlags {
    constructor(data: typeof AppProviderAPI.builderInfo.configFlags) {
        if (data === undefined) {
            return;
        }
        this.hideTimeClockGeneralJob = data.hideTimeClockGeneralJob;
        this.timeCardUseHoursMinutesFormat = data.timeCardUseHoursMinutesFormat;
    }
    timeCardUseHoursMinutesFormat: boolean;
    hideTimeClockGeneralJob: boolean;
}

export class MediaUploadCompressionSettings {
    constructor(data: Pick<MediaUploadCompressionSettings, "maxLongEdgePx" | "jpegQuality">) {
        this.maxLongEdgePx = data.maxLongEdgePx;
        this.jpegQuality = data.jpegQuality;
    }

    /** Longest side of the photo after resizing, in pixels. */
    maxLongEdgePx: number;
    /** JPEG encode quality, 0-1. */
    jpegQuality: number;
}

export class MarketplaceConfiguration {
    constructor(data: any) {
        this.auth0Domain = data.auth0Domain;
        this.auth0ClientId = data.auth0ClientId;
        this.auth0Audience = data.auth0Audience;
        this.paragonProjectId = data.paragonProjectId;
        this.hubSpotContactSyncWorkflowId = data.hubSpotContactSyncWorkflowId;
        this.paragonSdkLocation = data.paragonSdkLocation;
    }

    auth0Domain: string;
    auth0ClientId: string;
    auth0Audience: string;
    paragonProjectId: string;
    hubSpotContactSyncWorkflowId: string;
    paragonSdkLocation: string;
}

interface ILearningAcademyConfiguration {
    leads: string;
    schedule: string;
    toDos: string;
    dailyLogs: string;
    changeOrders: string;
    warranty: string;
    selections: string;
    timeClock: string;
    media: string;
    communications: string;
    estimates: string;
    bids: string;
    bills: string;
    purchaseOrders: string;
    budget: string;
    ownerInvoices: string;
    costCodes: string;
    projectManagement: string;
    takeOff: string;
}

export class LearningAcademyConfiguration implements ILearningAcademyConfiguration {
    constructor(data: ILearningAcademyConfiguration) {
        this.leads = data.leads;
        this.schedule = data.schedule;
        this.toDos = data.toDos;
        this.dailyLogs = data.dailyLogs;
        this.changeOrders = data.changeOrders;
        this.warranty = data.warranty;
        this.selections = data.selections;
        this.timeClock = data.timeClock;
        this.media = data.media;
        this.communications = data.communications;
        this.estimates = data.estimates;
        this.bids = data.bids;
        this.bills = data.bills;
        this.purchaseOrders = data.purchaseOrders;
        this.budget = data.budget;
        this.ownerInvoices = data.ownerInvoices;
        this.costCodes = data.costCodes;
        this.projectManagement = data.projectManagement;
        this.takeOff = data.takeOff;
    }

    leads: string;
    schedule: string;
    toDos: string;
    dailyLogs: string;
    changeOrders: string;
    warranty: string;
    selections: string;
    timeClock: string;
    media: string;
    communications: string;
    estimates: string;
    bids: string;
    bills: string;
    purchaseOrders: string;
    budget: string;
    ownerInvoices: string;
    costCodes: string;
    projectManagement: string;
    takeOff: string;
}

export class AppDefaultInfo {
    constructor(data: any | undefined) {
        // If data is undefined, assume that we are in the test environment
        // The default flag values are assumed
        if (data === undefined) {
            return;
        }

        Array.from(Object.entries(this)).forEach(([key, _]) => {
            const appDefaultKey = key as keyof AppDefaultInfo;
            const shouldReportKeyNotFound =
                !(appDefaultKey in data) && !appDefaultKey.startsWith("sentry");

            Object.defineProperty(this, appDefaultKey, {
                get() {
                    if (shouldReportKeyNotFound) {
                        console.error(
                            `An attempt was made to access an App Default Value [key="${appDefaultKey}"] that was not provided by the global info API endpoint. Check to make sure the App Default property is marked serialized on the backend or remove the key from the AppDefaultValues object on the frontend.`,
                        );
                        return true;
                    }

                    return data[key];
                },
            });

            // Don't fail during construction otherwise the entire solution would fail, but we do
            // want to throw errors in case a developer forgot to remove the key from this type
            if (shouldReportKeyNotFound) {
                console.error(
                    `An attempt was made to populate an App Default Value [key="${appDefaultKey}"] that was not provided by the global info API endpoint. Check to make sure the App Default property is marked serialized on the backend or remove the key from the AppDefaultValues object on the frontend.`,
                );
            }
        });
    }

    sentryReactDSN: string | undefined;
    useFeatureFlagBuilderPrefsForReads: boolean;
    importUIRefreshFrequency: number;
    sessionTimeoutMinutes: number;
    sentryReactSamplingRate: string;
    invisibleRecaptchaApiKey: string;
    enableCaptchaForSignups: boolean;
    enableCaptchaSitewide: boolean;
    mixpanelAnalyticsProjectId: string | undefined;
    enableGoogleMaps: boolean;
    enableGTMSignups: boolean;
    dotNetSentryURL: string;
    reactSentryURL: string;
    dataDogRUM: boolean;
    dataDogApplicationId: string | undefined;
    dataDogClientToken: string | undefined;
    dataDogSite: string | undefined;
    dataDogSessionSampleRate: number;
    dataDogPremiumSampleRate: number;
    dataDogTrackUserInteractions: boolean;
    dataDogEnableTracing: boolean;
    dataDogForceAiSearchReplay: boolean;
    sawmillIngestionEndpoint: string | undefined;
    sawmillFrontendBatchThreshold: number | undefined;
    sawmillFrontendMaxCachedEvents: number | undefined;
    sawmillFrontendMaxPayloadSizeBytes: number | undefined;
    enableSawmillFrontend: boolean;
    sawmillV3Configuration: SawmillConfiguration;
    enableChurnkey: boolean;
    enableParagon: boolean;
    enableQualtrics: boolean;
    qualtricsInterceptorUrl: string;
    qualtricsProjectId: string;
    enableMaze: boolean;
    mazeApiKey: string;
    unreadNotificationCountCacheTimeout: number;
    whatsNewPollInterval: number;
    marketplaceConfiguration: MarketplaceConfiguration;
    mediaUploadCompressionSettings: MediaUploadCompressionSettings =
        new MediaUploadCompressionSettings({
            maxLongEdgePx: 2500,
            jpegQuality: 0.5,
        });
    coreSpaConfiguration: CoreSpaConfiguration;
    chatPollingRateInMilliseconds: number = 5000;
    chatMainNavIndicatorPollingRateInSeconds: number = 60;
    internalUserActivationReminderIntervalInMinutes: number = 4320;
    internalUserActivationReminderMaxDaysInMinutes: number = 43200;
    subActivationReminderIntervalInMinutes: number = 4320;
    subActivationReminderMaxDaysInMinutes: number = 43200;
    maxSpecificationsForPrint: number;
    enablePpsJS: boolean;
    ppsSandboxFrontEndBaseUrl: string;
    ppsFrontEndBaseUrl: string;
    enableChameleon: boolean;
    chameleonToken: string | undefined;
    forceUpdateCredentials: boolean;
    billUploadTextDocumentsEnabled: boolean;
    hideOldBudgetDate: Date | undefined;
    forceAGGridSignupDate: Date | undefined;
    buildertrendLearningAcademyURL: string;
    pspdfkitLicenseKey: string;
    enableClamAVScansOnSignatures: boolean;
    learningAcademyConfig: LearningAcademyConfiguration;
    deployRefreshPromptWaitTime: number;
    deployRefreshRedirectWaitTime: number;
    hideLegacyBudgetOverrideBuilderIds: string;
    credentialUpgradeSkipDisabledDate: Date;
    enableServiceCloudChat: boolean;
    serviceCloudConfig: ServiceCloudConfig;
    aGGridLicenseKey: string =
        "Using_this_{AG_Grid}_Enterprise_key_{AG-089570}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{Buildertrend}_is_granted_a_{Multiple_Applications}_Developer_License_for_{45}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_need_to_be_licensed_in_addition_to_the_ones_working_with_{AG_Grid}_Enterprise___This_key_has_been_granted_a_Deployment_License_Add-on_for_{1}_Production_Environment___This_key_works_with_{AG_Grid}_Enterprise_versions_released_before_{28_July_2026}____[v3]_[01]_MTc4NTE5MzIwMDAwMA==e77164773b535bbfb4e683b5f440e351";
    enableAchPaymentMethod: boolean;
    enableCardPaymentMethod: boolean;
    communityURL: string | undefined;
    enableBillChangeTracking: boolean;
    enableChangeOrderChangeTracking: boolean;
    enableTimeClockChangeTracking: boolean;
    enableCustomerInvoiceChangeTracking: boolean;
    enableEstimateChangeTracking: boolean;
    enableTasksActivityLog: boolean;
    enableSelectionChoiceAndOptionTracking: boolean;
    showDocumentsPlansMigrationBanner: boolean;
    enableTaskImportFromFileAiMapping: boolean;
    mobileWebviewExperienceMinAppVersionForGlobalOptIn: string;
    searchServiceConfiguration: SearchServiceConfiguration = new SearchServiceConfiguration();
    enableNotificationsEmail: boolean;
    attachedFilesRemoveViewableByColumn: boolean;
    showChangeOrderFeedback: boolean;
    showEstimateWorksheetFeedback: boolean;
    tasksStaticShareLinks: boolean;
    hideOutboundWalletPaymentOption: boolean;
    showEstimateLearnMoreBanner: boolean;
}

export class SupportContactInfo {
    constructor(data: any) {
        this.email = data.email;
        this.header = data.header;
        this.phoneNumber = data.phoneNumber;
        this.workDays = data.workDays;
        this.workHours = data.workHours;
        this.timezone = data.timezone;
    }

    email: string;
    header: string;
    phoneNumber: string;
    workDays: string;
    workHours: string;
    timezone: string;
}

/**
 * A user's decision for every known opt-in feature, addressable by name — `optIns.infoArch`
 * rather than an index expression. Every `OptIn` member is present, so a consumer never has to
 * handle a missing key, and referencing one that doesn't exist is a compile error.
 *
 * Tri-state per feature: `true` opted in, `false` tried it and opted back out, `null` never
 * decided. Only `null` marks a user as eligible for prompting — do not collapse it into `false`.
 */
export type UserOptIns = Readonly<Record<OptIn, boolean | null>>;

/**
 * Every known opt-in, undecided. Derived from the generated `OptIn` union rather than written
 * out by hand, so adding a member to the C# `OptIn` enum flows through swagger to here on the
 * next handler regeneration with no TypeScript to update.
 *
 * Use this wherever a `UserInfo` is built without server data — tests, the legacy webforms
 * handler — instead of an object literal, which would need a new key per feature added.
 */
export const noOptInDecisions: UserOptIns = Object.freeze(
    Object.fromEntries(Object.values(OptIn).map((key) => [key, null])) as UserOptIns,
);

export class UserInfo {
    /**
     * `optIns` comes from the generated model instead of being inferred from
     * `AppProvider.api.json` like the rest of this parameter. A JSON fixture can only supply one
     * value per key, so it infers `boolean` or `null` but never the `boolean | null` the tri-state
     * actually is. The override restores the real contract; the fixture stays sample data.
     */
    constructor(data: Omit<typeof AppProviderAPI.userInfo, "optIns"> & { optIns: UserOptIns }) {
        this.timeoutMinutes = data.timeoutMinutes;
        this.globalUserId = data.globalUserId;
        this.userId = data.userId;
        this.orgLinks = (data.orgLinks ?? []).map((orgLink) => new OrgLinkInfo(orgLink));
        this.userName = data.userName;
        this.fullName = data.fullName;
        this.firstName = data.firstName;
        this.loginType = data.loginType;
        this.credentialLoginType = data.credentialLoginType;
        this.isImpersonatingBuilder = data.isImpersonatingBuilder;
        this.isAdmin = data.isAdmin;
        this.isBuilderImpersonatingOwner = data.isBuilderImpersonatingOwner;
        this.systemAlerts = data.systemAlerts;
        this.supportChatSettings = data.supportChatSettings
            ? new ServiceCloudSettings(data.supportChatSettings)
            : null;
        this.roleInfo = data.roleInfo ? new RoleInfo(data.roleInfo) : null;
        this.shouldRedirectToSetupExperience = data.shouldRedirectToSetupExperience ?? false;
        this.hasAcceptedTermsAndPolicy = data.hasAcceptedTermsAndPolicy ?? false;
        this.optIns = data.optIns;
        this.permissions = data.permissions ? new UserTabPermissions(data.permissions) : null;
        this.email = data.email ?? null;
    }

    loginType: BTLoginTypes;
    credentialLoginType: BTLoginTypes;
    timeoutMinutes: number;
    isImpersonatingBuilder: boolean;
    isBuilderImpersonatingOwner: boolean;
    systemAlerts: IAlert<IHeaderAlertExtraData>[];

    // Only populated when user is in session
    globalUserId: number | null;
    userId: number | null;
    orgLinks: OrgLinkInfo[] | null;
    userName: string | null;
    fullName: string | null;
    firstName: string | null;
    email: string | null;

    /**
     * @deprecated Use email instead.  This was populated incorrectly, only populated in non-SPA (AKA webforms).  Delete eventually.
     */
    legacyEmailDoNotUse: string | null;
    isAdmin: boolean | null;
    roleInfo: RoleInfo | null;
    shouldRedirectToSetupExperience: boolean;
    hasAcceptedTermsAndPolicy: boolean;
    /** @see {@link UserOptIns} — e.g. `userInfo.optIns.infoArch`. */
    optIns: UserOptIns;
    permissions: UserTabPermissions | null;
    supportChatSettings: ServiceCloudSettings | null;
}

export class BuilderInfo {
    constructor(data: typeof AppProviderAPI.builderInfo) {
        this.encryptedBuilderId = data.encryptedBuilderId ?? null;
        this.builderId = data.builderId;
        this.name = data.name;
        this.weekStartDay = data.weekStartDay;
        this.scheduleDefaultTabForCurrentPortal = data.scheduleDefaultTabForCurrentPortal;
        this.selectionDefaultBuilderView = data.selectionDefaultBuilderView;
        this.activityCalendarDefaultView = data.activityCalendarDefaultView;
        this.isDemoAccount = data.isDemoAccount;
        this.locale = new BuilderLocale(data.locale);
        const accountFlagsData = (data as any).accountFlags as Record<string, boolean> | undefined;
        this.flags = GetBuilderFlags(
            (data as any).flags as Record<string, boolean> | undefined,
            accountFlagsData,
        );
        this.accountFlags = new AccountFlags(accountFlagsData);
        this.configFlags = new BuilderConfigFlags(data.configFlags);
        this.taxesOptOut = data.taxesOptOut;
        this.setupDate = moment(data.setupDate);
        this.showAccountingBannerAd = data.showAccountingBannerAd;
        this.canUseExternalTemplates = data.canUseExternalTemplates ?? false;
        this.redirectToSetupExperience = data.redirectToSetupExperience ?? false;
        this.loginsDisabled = data.loginsDisabled ?? false;
        this.hasJobGroups = data.hasJobGroups ?? false;
        this.timeClockSettings = data.timeClockSettings;
        this.hasLegacySelectionsData = data.hasLegacySelectionsData ?? false;
        this.isFullySelectionsReimagined = data.isFullySelectionsReimagined ?? false;
        this.newJobsUseSelectionsReimagined = data.newJobsUseSelectionsReimagined ?? false;
    }

    encryptedBuilderId: string | null;
    builderId: number;
    name: string;
    weekStartDay: number;
    scheduleDefaultTabForCurrentPortal: number;
    selectionDefaultBuilderView: number;
    activityCalendarDefaultView: number;
    isDemoAccount: boolean;
    locale: BuilderLocale;
    flags: BuilderFlags;
    accountFlags: AccountFlags;
    configFlags: BuilderConfigFlags;
    taxesOptOut: boolean;
    setupDate: moment.Moment;
    showAccountingBannerAd?: boolean;
    canUseExternalTemplates: boolean;
    redirectToSetupExperience: boolean;
    loginsDisabled: boolean;
    hasJobGroups: boolean;
    timeClockSettings: TimeClockSettingsResponse;
    hasLegacySelectionsData: boolean;
    isFullySelectionsReimagined: boolean;
    newJobsUseSelectionsReimagined: boolean;
}

export class GlobalInfo {
    constructor(data: typeof AppProviderAPI) {
        this.thirdPartyInfo = new ThirdPartyInfo(data.thirdPartyInfo);
        this.environmentInfo = new EnvironmentInfo(data.environmentInfo);
        this.appDefaultInfo = new AppDefaultInfo((data as any).appDefaultInfo);
        SetUseAccountFlagsAsSourceDefault(
            this.appDefaultInfo?.useFeatureFlagBuilderPrefsForReads ?? false,
        );
        this.userInfo = new UserInfo(data.userInfo);

        this.builderInfo = data.builderInfo ? new BuilderInfo(data.builderInfo) : null;
        this.integrationBuilderProfile = data.integrationBuilderProfile
            ? new IntegrationBuilderProfile(data.integrationBuilderProfile)
            : null;
        this.supportContactInfo = (data as any).supportContactInfo
            ? new SupportContactInfo((data as any).supportContactInfo)
            : null;
        this.brandingInfo = data.brandingInfo ? new BrandingInfo(data.brandingInfo) : null;
        this.integrationUserProfile = data.integrationUserProfile
            ? new IntegrationUserProfile(data.integrationUserProfile)
            : null;
    }

    // All
    thirdPartyInfo: ThirdPartyInfo;
    appDefaultInfo: AppDefaultInfo;
    environmentInfo: EnvironmentInfo;
    userInfo: UserInfo;

    // Requires builder in session
    builderInfo: BuilderInfo | null;
    supportContactInfo: SupportContactInfo | null;
    brandingInfo: BrandingInfo | null;
    integrationBuilderProfile: IntegrationBuilderProfile | null;

    // Requires user in session
    integrationUserProfile: IntegrationUserProfile | null;
}
